import { Author } from 'z3k-parser/model/author';
import { TypeCastError } from './types';
import { assertNever } from 'webui/utils/never';
import { isArray } from 'utils';
import { partial } from 'webui/utils/functions';
import { getAuthor, ObjectLine } from 'z3k-parser/parser/utils/object-line';
export function addRest(array, startWith) {
    const rest = array.slice(startWith);
    if (rest.length > 0) {
        return { rest: rest };
    }
    else {
        return {};
    }
}
function fromStr2Z3KObject(type_spec, object_id, params) {
    return {
        id: object_id,
        ...Object.fromEntries(type_spec.map(({ name, type, default_from_op, fromOP }, i) => {
            const param = params[i];
            return [
                name,
                params.length > i
                    ? fromOP(cast(param, type))
                    : default_from_op,
            ];
        })),
        ...addRest(params, type_spec.length),
    };
}
function Z3KObject2Str(type_spec, object_id, object) {
    return new ObjectLine(object.id, object_id, type_spec.map(({ name, toOP }) => toOP(object[name])));
}
export function getFieldParser(spec) {
    return {
        toStr: (z3k_object_id, object) => Z3KObject2Str(spec, z3k_object_id, object),
        fromStr: (object_id, params) => fromStr2Z3KObject(spec, object_id, params),
    };
}
function checkGoodObjectID(x) {
    return getAuthor(x) !== Author.INVALID;
}
function cast(x, type) {
    switch (type) {
        case 'number': {
            if (typeof x != 'number') {
                throw new TypeCastError(`typeof ${x} is not ${type}`);
            }
            return x;
        }
        case 'number_array': {
            if (!isArray(x)) {
                throw new TypeCastError(`typeof ${x} is not ${type}`);
            }
            if (x.some((item) => typeof item !== 'number')) {
                throw new TypeCastError(`someone in number_array ${x} is not number`);
            }
            return x;
        }
        case 'string': {
            if (typeof x != 'string') {
                throw new TypeCastError(`typeof ${x} is not ${type}`);
            }
            return x;
        }
        case 'objectid': {
            if (typeof x != 'number') {
                throw new TypeCastError(`bad number, typeof ${x} is not ${type}`);
            }
            if (x !== 0 && !checkGoodObjectID(x)) {
                throw new TypeCastError(`bad id, typeof ${x} is not ${type}`);
            }
            return x;
        }
        case 'objectid_or_null': {
            if (typeof x != 'number') {
                throw new TypeCastError(`bad number, typeof ${x} is not ${type}`);
            }
            if (x !== 0 && !checkGoodObjectID(x)) {
                throw new TypeCastError(`bad id, typeof ${x} is not ${type}`);
            }
            return x === 0 ? null : x;
        }
        case 'objectid_array': {
            if (!isArray(x)) {
                throw new TypeCastError(`typeof ${x} is not ${type}`);
            }
            if (x.some((item) => typeof item !== 'number' ||
                !checkGoodObjectID(item))) {
                throw new TypeCastError(`someone in objectid_array ${x} is not object_id`);
            }
            return x;
        }
        default:
            assertNever(type);
    }
    return x;
}
function defaultFromOP(x) {
    return x;
}
function defaultToOP(x) {
    return x;
}
export const number_or_0 = {
    type: 'number',
    default_from_op: 0,
    fromOP: defaultFromOP,
    toOP: defaultToOP,
};
export const number_array_or_empty = {
    type: 'number_array',
    default_from_op: [],
    fromOP: defaultFromOP,
    toOP: defaultToOP,
};
export const string_or_empty = {
    type: 'string',
    default_from_op: '',
    fromOP: defaultFromOP,
    toOP: defaultToOP,
};
export const objectid_or_0 = {
    type: 'objectid',
    default_from_op: 0,
    fromOP: defaultFromOP,
    toOP: defaultToOP,
};
function objNullToOP(x) {
    return (x == null ? 0 : x);
}
export const objectid_or_null = {
    type: 'objectid_or_null',
    default_from_op: 0,
    fromOP: defaultFromOP,
    toOP: objNullToOP,
};
export const objectid_array_or_empty = {
    type: 'objectid_array',
    default_from_op: [],
    fromOP: defaultFromOP,
    toOP: defaultToOP,
};
export function getValue(array, index, dflt) {
    return array[index] ?? dflt;
}
export function nonZero(array, index, dflt = 0) {
    const value = getValue(array, index, dflt);
    return value === 0 ? null : value;
}
function physicalFromOP(x) {
    return (x === 255 ? -1 : x);
}
function physicalToOP(x) {
    return (x === -1 ? 255 : x);
}
export const physical_converters = {
    type: 'number',
    default_from_op: 0,
    fromOP: physicalFromOP,
    toOP: physicalToOP,
};
function multiFromOP(multiplier, x) {
    return (x / multiplier);
}
function multiToOP(multiplier, x) {
    return Math.round(x * multiplier);
}
export const multi_converters = (multiplier) => ({
    type: 'number',
    default_from_op: 0,
    fromOP: partial(multiFromOP, multiplier),
    toOP: partial(multiToOP, multiplier),
});
export const time_converters = multi_converters(60000);
export const calibration_shift_converters = {
    ...number_or_0,
    fromOP: (x) => (x < 128 ? +x / 10 : -(256 - +x) / 10),
    toOP: (x) => Math.round(x * 10) >= 0 ? Math.round(x * 10) : 256 + Math.round(x * 10),
};
export function hasThreshold(value) {
    return value !== null && value !== '';
}
function thresholdFromOP(x) {
    return (x === 0 ? '' : x / 10 - 273);
}
function thresholdToOP(x) {
    return (hasThreshold(x)
        ? Math.round((Number(x) + 273) * 10)
        : 0);
}
export const threshold_converters = {
    type: 'number',
    default_from_op: 0,
    fromOP: thresholdFromOP,
    toOP: thresholdToOP,
};
function thresholdFromOPRight(x) {
    return (x === 0 || x === '' ? null : x / 10 - 273);
}
export const threshold_right_converters = {
    type: 'number',
    default_from_op: 0,
    fromOP: thresholdFromOPRight,
    toOP: thresholdToOP,
};
